import { useEffect, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { profileI18n } from './i18n';
import {
  getUserById,
  updateUserData,
} from '../../../../services/myDataService';
import { getCurrentUser } from '../../../../services/authService';
import ProfileImageUploader from '../../../../components/ProfileImageUploader/ProfileImageUploader';
import {
  getConnectionStatus,
  getConnectUrl,
  postDisconnect,
} from 'services/scheduleServices';
import { useChangePassword } from 'hooks/auth/useChangePassword';
import { validatePassword, isPasswordValid } from 'helpers/passwordValidator';
import outlook from '../../../../assets/images/Outlook-Icon-FY26.svg';

const Profile = ({ DesignSystemComponents }) => {
  const { Card, Button, Input } = DesignSystemComponents;
  const { t } = useScopedTranslation(profileI18n);

  const [userId, setUserId] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);

  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    email: '',
    phone: '',
    position: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isEditing, setIsEditing] = useState(false);

  const { changePassword, isLoading: changingPassword } = useChangePassword();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [passwordMessage, setPasswordMessage] = useState({
    text: '',
    type: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [connectingOutlook, setConnectingOutlook] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [disconnectingOutlook, setDisconnectingOutlook] = useState(false);

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    } else {
      setLoading(false);
      setMessage({
        text: t('pf_msg_noUser'),
        type: 'error',
      });
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchUserData();
  }, [userId]);

  const handleConnectOutlook = async () => {
    try {
      setConnectingOutlook(true);
      setMessage({ text: '', type: '' });

      const data = await getConnectUrl.getConnectUrl();

      if (!data?.url) {
        throw new Error('No connection URL received');
      }

      window.location.href = data.url;
    } catch (error) {
      setMessage({
        text:
          error.customMessage ||
          error.message ||
          t('pf_outlookConnectError'),
        type: 'error',
      });
    } finally {
      setConnectingOutlook(false);
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const data = await getUserById(userId);

      setUserData(data);
      setProfileImageFile(null);

      setFormData({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        position: data.position || '',
        alias: data.alias || '',
      });

      setIsEditing(false);
    } catch {
      setMessage({
        text: t('pf_msg_loadError'),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing) return;

    try {
      setSaving(true);
      setMessage({ text: '', type: '' });

      const payload = new FormData();
      payload.append('Name', formData.name);
      payload.append('Email', formData.email);
      payload.append('Phone', formData.phone);
      payload.append('Position', formData.position);
      payload.append('Alias', formData.alias);

      if (profileImageFile) payload.append('ProfileImage', profileImageFile);

      await updateUserData(userId, payload);

      setMessage({
        text: t('pf_msg_updated'),
        type: 'success',
      });

      await fetchUserData();
    } catch (error) {
      setMessage({
        text: error.message || t('pf_msg_updateError'),
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const fetchOutlookConnection = async () => {
    try {
      const result = await getConnectionStatus.getStatus();
      setOutlookConnected(result);
    } catch (error) {
      console.warn('Error loading Outlook connection status:', error);
    }
  };

  const scrollToTop = () => {
    const container = document.querySelector('.content-inner');
    if (container) {
      container.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleDisconnectOutlook = async () => {
    try {
      setDisconnectingOutlook(true);
      setMessage({ text: '', type: '' });

      await postDisconnect.postDisconnect();
      await fetchOutlookConnection();

      setMessage({
        text: t('pf_outlookDisconnected'),
        type: 'success',
      });

      setTimeout(() => {
        scrollToTop();
      }, 50);
    } catch (error) {
      setMessage({
        text:
          error.customMessage ||
          error.message ||
          t('pf_outlookDisconnectError'),
        type: 'error',
      });

      setTimeout(() => {
        scrollToTop();
      }, 50);
    } finally {
      setDisconnectingOutlook(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchOutlookConnection();
  }, [userId]);

  if (!Card || !Button || !Input) return null;

  if (loading) {
    return (
      <Card specialPadding='p-4'>
        <div className='d-flex justify-content-center align-items-center'>
          <p>{t('pf_loading')}</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className='mb-4'>
        <h2 className='fw-semibold'>{t('pf_pageTitle')}</h2>
      </div>
      <Card specialPadding='p-4' className='mb-4'>
        {message.text && (
          <div
            className='mb-3 p-3'
            style={{
              backgroundColor:
                message.type === 'success' ? '#d4edda' : '#f8d7da',
              color: message.type === 'success' ? '#155724' : '#721c24',
              borderRadius: '8px',
              border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            }}
          >
            {message.text}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-6'>
              <h5 className='mb-3'>{t('pf_personalData')}</h5>
              <div className='row'>
                <div className='col-6'>
                  <ProfileImageUploader
                    value={userData?.profileImageUrl}
                    onChange={setProfileImageFile}
                    disabled={!isEditing || saving}
                  />
                </div>
                <div className='col-6'>
                  <Input
                    type='text'
                    name='alias'
                    value={formData.alias}
                    onChange={handleChange}
                    placeholder={t('pf_aliasPlaceholder')}
                    label={t('pf_aliasLabel')}
                    disabled={!isEditing || saving}
                    width='100%'
                  />
                </div>
              </div>
              <div className='row'>
                <div className='col-12'>
                  <Input
                    type='text'
                    name='name'
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('pf_namePlaceholder')}
                    label={t('pf_nameLabel')}
                    disabled={!isEditing || saving}
                  />
                </div>
                <div className='col-12'>
                  <Input
                    type='text'
                    name='position'
                    value={formData.position}
                    onChange={handleChange}
                    placeholder={t('pf_positionPlaceholder')}
                    label={t('pf_positionLabel')}
                    disabled={!isEditing || saving}
                  />
                </div>
              </div>
              <div className='row'>
                <div className='col-12'>
                  <Input
                    type='email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('pf_emailPlaceholder')}
                    label={t('pf_emailLabel')}
                    disabled={!isEditing || saving}
                  />
                </div>
                <div className='col-12'>
                  <Input
                    type='tel'
                    name='phone'
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('pf_phonePlaceholder')}
                    label={t('pf_phoneLabel')}
                    disabled={!isEditing || saving}
                  />
                </div>
              </div>
            </div>
            <div className='col-6'>
              <div className='d-flex h-100 flex-column justify-content-end'>
                <div className='d-flex justify-content-end'>
                  {!isEditing ? (
                    <Button
                      label={t('pf_edit')}
                      type='button'
                      variant='primary'
                      onClick={(e) => {
                        if (e) e.preventDefault();
                        setIsEditing(true);
                        setMessage({ text: '', type: '' });
                      }}
                    />
                  ) : (
                    <div className='d-flex gap-3'>
                      <Button
                        label={t('pf_cancel')}
                        type='button'
                        variant='secondary'
                        disabled={saving}
                        onClick={(e) => {
                          if (e) e.preventDefault();
                          setFormData({
                            name: userData.name || '',
                            email: userData.email || '',
                            phone: userData.phone || '',
                            position: userData.position || '',
                          });
                          setMessage({ text: '', type: '' });
                          setIsEditing(false);
                        }}
                      />
                      <Button
                        label={saving ? t('pf_saving') : t('pf_saveChanges')}
                        type='submit'
                        variant='primary'
                        disabled={saving}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </Card>
      <Card specialPadding='p-4' className='mb-4'>
        {passwordMessage.text && (
          <div
            className='mb-3 p-3'
            style={{
              backgroundColor:
                passwordMessage.type === 'success' ? '#d4edda' : '#f8d7da',
              color: passwordMessage.type === 'success' ? '#155724' : '#721c24',
              borderRadius: '8px',
              border: `1px solid ${passwordMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            }}
          >
            {passwordMessage.text}
          </div>
        )}
        <h5 className='mb-3'>{t('pf_changePassword')}</h5>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setPasswordMessage({ text: '', type: '' });

            if (
              !passwordForm.currentPassword ||
              !passwordForm.newPassword ||
              !passwordForm.confirmNewPassword
            ) {
              setPasswordMessage({
                text: t('pf_pwd_required'),
                type: 'error',
              });
              return;
            }
            if (!isPasswordValid(passwordForm.newPassword)) {
              setPasswordMessage({
                text: t('pf_pwd_invalid'),
                type: 'error',
              });
              return;
            }
            if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
              setPasswordMessage({
                text: t('pf_pwd_mismatch'),
                type: 'error',
              });
              return;
            }

            try {
              await changePassword(
                passwordForm.currentPassword,
                passwordForm.newPassword
              );
              setPasswordMessage({
                text: t('pf_pwd_success'),
                type: 'success',
              });
              setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: '',
              });
            } catch (err) {
              setPasswordMessage({
                text: t('pf_pwd_error', { message: err.response?.data?.message || '' }),
                type: 'error',
              });
            }
          }}
        >
          <div className='row'>
            <div className='col-4'>
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                name='currentPassword'
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    currentPassword: e.target.value,
                  }))
                }
                placeholder={t('pf_currentPasswordPlaceholder')}
                label={t('pf_currentPasswordLabel')}
                disabled={changingPassword}
                icon={showCurrentPassword ? 'eye' : 'eyeSlash'}
                iconOnClick={() => setShowCurrentPassword((prev) => !prev)}
              />
            </div>
            <div className='col-4'>
              <Input
                type={showNewPassword ? 'text' : 'password'}
                name='newPassword'
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    newPassword: e.target.value,
                  }))
                }
                placeholder={t('pf_newPasswordPlaceholder')}
                label={t('pf_newPasswordLabel')}
                disabled={changingPassword}
                icon={showNewPassword ? 'eye' : 'eyeSlash'}
                iconOnClick={() => setShowNewPassword((prev) => !prev)}
              />
            </div>
            <div className='col-4'>
              <Input
                type={showConfirmNewPassword ? 'text' : 'password'}
                name='confirmNewPassword'
                value={passwordForm.confirmNewPassword}
                onChange={(e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    confirmNewPassword: e.target.value,
                  }))
                }
                placeholder={t('pf_confirmPasswordPlaceholder')}
                label={t('pf_confirmPasswordLabel')}
                disabled={changingPassword}
                icon={showConfirmNewPassword ? 'eye' : 'eyeSlash'}
                iconOnClick={() => setShowConfirmNewPassword((prev) => !prev)}
              />
            </div>
          </div>
          {passwordForm.newPassword && (
            <div className='mt-2 mb-2'>
              <p className='mini fw-semibold mb-1'>{t('pf_rule_title')}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {validatePassword(passwordForm.newPassword).map(({ key, passed }) => (
                  <li
                    key={key}
                    className='mini d-flex align-items-center gap-1'
                    style={{ color: passed ? 'var(--success-500, #198754)' : 'var(--error-500, #dc3545)' }}
                  >
                    <span>{passed ? '✓' : '✗'}</span>
                    <span>{t(`pf_rule_${key}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className='d-flex justify-content-end mt-2'>
            <Button
              label={changingPassword ? t('pf_changingPassword') : t('pf_changePasswordBtn')}
              type='submit'
              variant='primary'
              disabled={changingPassword || !isPasswordValid(passwordForm.newPassword)}
            />
          </div>
        </form>
      </Card>
      <Card specialPadding='p-4'>
        <h5 className='mb-3'>{t('pf_connectTitle')}</h5>
        <div className='row'>
          <div className='col-4'>
            <Card specialPadding='p-4'>
              <div className='d-flex align-items-center mb-2 justify-content-between'>
                <div className='d-flex gap-2 align-items-center'>
                  <img
                    className='integration-card__logo'
                    src={outlook}
                    alt='outlook'
                  />
                  <h3 className='mb-0 fw-semibold'>Outlook</h3>
                </div>
                {outlookConnected.connected === false ? (
                  <Button
                    label={t('pf_outlookConnect')}
                    onClick={handleConnectOutlook}
                    variant='naked'
                    disabled={connectingOutlook}
                  />
                ) : (
                  <Button
                    label={t('pf_outlookDisconnect')}
                    onClick={handleDisconnectOutlook}
                    variant='naked'
                    disabled={disconnectingOutlook}
                  />
                )}
              </div>
              <p className='body'>
                {t('pf_outlookDescription')}
              </p>
            </Card>
          </div>
        </div>
      </Card>
    </>
  );
};

export default Profile;
